import type { LeadSignupSource } from '../../server/leadSignup'

/* One flag for every gate on the website. The value predates the webinars;
   renaming it would re-gate everyone who already unlocked the ebook. */
export const LEAD_GATE_ACCESS_KEY = 'ebook-company-brain-access'

/* The signed token from the last form submit, so a gate that opens without
   its form (access was granted elsewhere) can still report the unlock to
   Loops for that person. */
const LEAD_GATE_UNLOCK_TOKEN_KEY = 'lead-gate-unlock-token'
const LEAD_GATE_REPORTED_UNLOCKS_KEY = 'lead-gate-reported-unlocks'

/* ?access=slite-team skips the form for the team. It ships in the client
   bundle, so it is a convenience, not access control. */
const LEAD_GATE_BYPASS_PARAM = 'access'
const LEAD_GATE_BYPASS_VALUE = 'slite-team'

export function grantLeadGateAccess() {
  try {
    localStorage.setItem(LEAD_GATE_ACCESS_KEY, '1')
  } catch {
    // localStorage can throw in private mode
  }
}

export function hasLeadGateAccess() {
  try {
    return localStorage.getItem(LEAD_GATE_ACCESS_KEY) === '1'
  } catch {
    return false
  }
}

/* Drops the param afterwards so it doesn't travel on in copied links. */
export function consumeLeadGateBypassParam() {
  const url = new URL(window.location.href)
  const isBypass =
    url.searchParams.get(LEAD_GATE_BYPASS_PARAM) === LEAD_GATE_BYPASS_VALUE
  if (!isBypass) return false

  grantLeadGateAccess()
  url.searchParams.delete(LEAD_GATE_BYPASS_PARAM)
  window.history.replaceState(
    null,
    '',
    `${url.pathname}${url.search}${url.hash}`
  )
  return true
}

export type LeadGateUnlock = {
  source: LeadSignupSource
  contentSlug: string
}

function unlockKey({ source, contentSlug }: LeadGateUnlock) {
  return `${source}:${contentSlug}`
}

function readReportedUnlocks(): string[] {
  try {
    const stored = JSON.parse(
      localStorage.getItem(LEAD_GATE_REPORTED_UNLOCKS_KEY) ?? '[]'
    )
    return Array.isArray(stored) ? stored : []
  } catch {
    return []
  }
}

function markUnlockReported(unlock: LeadGateUnlock) {
  try {
    const reported = new Set(readReportedUnlocks()).add(unlockKey(unlock))
    localStorage.setItem(
      LEAD_GATE_REPORTED_UNLOCKS_KEY,
      JSON.stringify([...reported])
    )
  } catch {
    // localStorage can throw in private mode
  }
}

/* Dev runs and dropped bots answer without a token. */
export async function readUnlockToken(response: Response) {
  try {
    const { unlockToken } = await response.json()
    return typeof unlockToken === 'string' ? unlockToken : null
  } catch {
    return null
  }
}

/* Called after a successful form submit: the signup already sent this unlock. */
export function rememberLeadGateSignup(
  unlockToken: string | null,
  unlock: LeadGateUnlock | null
) {
  if (unlockToken) {
    try {
      localStorage.setItem(LEAD_GATE_UNLOCK_TOKEN_KEY, unlockToken)
    } catch {
      // localStorage can throw in private mode
    }
  }
  if (unlock) markUnlockReported(unlock)
}

/* Reports an unlock that happened without the form, once per browser.
   Without a remembered token (access from the team bypass, or granted before
   tokens were remembered) there is no one to report it for. */
export async function reportLeadGateUnlock(unlock: LeadGateUnlock) {
  let unlockToken: string | null
  try {
    unlockToken = localStorage.getItem(LEAD_GATE_UNLOCK_TOKEN_KEY)
  } catch {
    return
  }
  if (!unlockToken || readReportedUnlocks().includes(unlockKey(unlock))) return
  try {
    const response = await fetch('/website/api/lead-unlock', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...unlock, unlockToken }),
    })
    if (response.ok) markUnlockReported(unlock)
  } catch {
    // Retried on the next visit.
  }
}
